import { useEffect, useRef, useState } from 'react';

import {
  CENSUS_STATUS_MAX_POLLS,
  CENSUS_STATUS_POLL_INTERVAL_MS,
  clearStoredCensusEntry,
  getActivationCensusValidationErrors,
  getActivationCensusValidationStatus,
  getStoredCensusEntry,
  isCensusParseError,
  isCensusRequestNotFound,
  isCensusValidationCompleted,
  resolveCompletedStep,
  sendActivationCensusToValidate,
  setStoredCensusEntry,
  type ActivationCensusStep,
  type IActivationPriceMismatch
} from './activationCensus';
import type {
  IActivationCensusErrorsFile,
  IActivationCensusValidationErrorsResult,
  IActivationCensusValidationSummary,
  IQuickCensusAgeSummaryEntry
} from '../../interfaces/quotes';
import { CensusVariant } from '../../types/quotes';

interface UseActivationCensusFlowArgs {
  brokerId: string | null;
  prospectId: number;
  quoteId: number;
  quoteIds: number[];
  appliedQuoteIds: number[];
  requiresMinimum?: boolean;
  variant?: CensusVariant;
  resolvePriceMismatch?: (
    result: IActivationCensusValidationErrorsResult,
    quoteId: number
  ) => Promise<IActivationPriceMismatch | null> | IActivationPriceMismatch | null;
}

interface IActivationCensusEntry {
  step: ActivationCensusStep;
  file: File | null;
  fileName: string | null;
  requestId: string | null;
  summary: IActivationCensusValidationSummary | null;
  errorsFile: IActivationCensusErrorsFile | null;
  distribution: IQuickCensusAgeSummaryEntry[] | null;
  priceMismatch: IActivationPriceMismatch | null;
}

const DEFAULT_ENTRY: IActivationCensusEntry = {
  step: 'upload',
  file: null,
  fileName: null,
  requestId: null,
  summary: null,
  errorsFile: null,
  distribution: null,
  priceMismatch: null
};

export interface ActivationCensusFlow {
  step: ActivationCensusStep;
  file: File | null;
  fileName: string | null;
  requestId: string | null;
  summary: IActivationCensusValidationSummary | null;
  errorsFile: IActivationCensusErrorsFile | null;
  distribution: IQuickCensusAgeSummaryEntry[] | null;
  priceMismatch: IActivationPriceMismatch | null;
  isRunning: boolean;
  setFile: (file: File | null) => void;
  handleFileSelect: (file: File) => void;
  handleUpload: () => void;
  handleRetry: () => void;
  refreshErrorsFile: () => void;
  acceptNewPrice: () => void;
  rejectNewPrice: () => void;
  reset: () => void;
}

export function useActivationCensusFlow({
  brokerId,
  prospectId,
  quoteId,
  quoteIds,
  appliedQuoteIds,
  requiresMinimum = true,
  variant = CensusVariant.Insurance,
  resolvePriceMismatch
}: UseActivationCensusFlowArgs): ActivationCensusFlow {
  const [entries, setEntries] = useState<Record<number, IActivationCensusEntry>>({});
  const entry = entries[quoteId] ?? DEFAULT_ENTRY;

  const activeRef = useRef(true);
  const timers = useRef<Record<number, ReturnType<typeof setTimeout> | undefined>>({});
  const runTokens = useRef<Record<number, number>>({});
  const fetchingErrorsFile = useRef<Record<number, boolean>>({});

  const resolvePriceMismatchRef = useRef(resolvePriceMismatch);
  useEffect(() => {
    resolvePriceMismatchRef.current = resolvePriceMismatch;
  });

  useEffect(() => {
    activeRef.current = true;
    const scheduled = timers.current;
    return () => {
      activeRef.current = false;
      Object.values(scheduled).forEach((timer) => timer && clearTimeout(timer));
    };
  }, []);

  const patch = (id: number, changes: Partial<IActivationCensusEntry>) =>
    setEntries((prev) => ({ ...prev, [id]: { ...(prev[id] ?? DEFAULT_ENTRY), ...changes } }));

  useEffect(() => {
    Object.entries(entries).forEach(([key, quoteEntry]) => {
      if (!quoteEntry.requestId) return;
      setStoredCensusEntry(Number(key), { requestId: quoteEntry.requestId, fileName: quoteEntry.fileName });
    });
  }, [entries]);

  const clearPoll = (id: number) => {
    const timer = timers.current[id];
    if (timer) clearTimeout(timer);
    delete timers.current[id];
  };

  const schedulePoll = (id: number, run: () => void) => {
    timers.current[id] = setTimeout(run, CENSUS_STATUS_POLL_INTERVAL_MS);
  };

  const startRun = (id: number): number => {
    clearPoll(id);
    const token = (runTokens.current[id] ?? 0) + 1;
    runTokens.current[id] = token;
    return token;
  };

  const isStale = (id: number, token: number) => !activeRef.current || runTokens.current[id] !== token;

  const failWithService = (id: number, token: number, changes: Partial<IActivationCensusEntry> = {}) => {
    if (!isStale(id, token)) patch(id, { ...changes, step: 'error-service' });
  };

  const dropUnknownRequest = (id: number, token: number) => {
    if (isStale(id, token)) return;
    clearStoredCensusEntry(id);
    patch(id, DEFAULT_ENTRY);
  };

  const fetchStatus = (id: number, requestId: string, token: number, resuming: boolean) =>
    getActivationCensusValidationStatus(brokerId, requestId).catch((error) => {
      if (resuming && isCensusRequestNotFound(error)) {
        dropUnknownRequest(id, token);
      } else {
        failWithService(id, token);
      }
      return null;
    });

  const pollStatus = async (id: number, requestId: string, token: number, attempt: number, resuming = false) => {
    const status = await fetchStatus(id, requestId, token, resuming);
    if (!status) return;
    if (isStale(id, token)) return;

    if (!isCensusValidationCompleted(status)) {
      if (attempt >= CENSUS_STATUS_MAX_POLLS) {
        failWithService(id, token);
        return;
      }
      schedulePoll(id, () => pollStatus(id, requestId, token, attempt + 1, resuming));
      return;
    }

    let result;
    try {
      result = await getActivationCensusValidationErrors(brokerId, requestId, variant);
    } catch {
      failWithService(id, token);
      return;
    }
    if (isStale(id, token)) return;

    const completed = { summary: result.summary, errorsFile: result.file, distribution: result.distribution ?? null };

    const step = resolveCompletedStep(result.summary, status.hasError, requiresMinimum);
    const reprice = resolvePriceMismatchRef.current;
    if (step !== 'success' || !reprice) {
      patch(id, { ...completed, step });
      return;
    }

    let priceMismatch: IActivationPriceMismatch | null;
    try {
      priceMismatch = await reprice(result, id);
    } catch {
      failWithService(id, token, completed);
      return;
    }
    if (isStale(id, token)) return;

    patch(id, { ...completed, priceMismatch, step: priceMismatch ? 'price-mismatch' : 'success' });
  };

  const runValidation = async (target: File) => {
    const id = quoteId;
    const token = startRun(id);
    patch(id, {
      step: 'validating',
      requestId: null,
      summary: null,
      errorsFile: null,
      distribution: null,
      priceMismatch: null
    });

    let sent;
    try {
      sent = await sendActivationCensusToValidate({ brokerId, prospectId, quoteId: id, file: target, variant });
    } catch (error) {
      if (!isStale(id, token)) patch(id, { step: isCensusParseError(error) ? 'error-format' : 'error-service' });
      return;
    }
    if (isStale(id, token)) return;

    patch(id, { step: 'processing', requestId: sent.requestId });
    pollStatus(id, sent.requestId, token, 1);
  };

  const resumeStoredValidation = (id: number, requestId: string, fileName: string | null) => {
    const token = startRun(id);
    patch(id, { step: 'processing', requestId, fileName });
    pollStatus(id, requestId, token, 1, true);
  };

  useEffect(() => {
    quoteIds.forEach((id) => {
      if (entries[id]) return;

      if (appliedQuoteIds.includes(id)) {
        clearStoredCensusEntry(id);
        return;
      }

      const stored = getStoredCensusEntry(id);
      if (!stored) return;

      resumeStoredValidation(id, stored.requestId, stored.fileName);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteIds, appliedQuoteIds]);

  const setFile = (file: File | null) => patch(quoteId, { file, fileName: file?.name ?? null });

  const refreshErrorsFile = () => {
    const id = quoteId;
    const { requestId, errorsFile } = entry;
    if (!requestId || errorsFile || fetchingErrorsFile.current[id]) return;

    fetchingErrorsFile.current[id] = true;
    getActivationCensusValidationErrors(brokerId, requestId, variant)
      .then((result) => {
        if (activeRef.current) patch(id, { errorsFile: result.file });
      })
      .catch(() => {})
      .finally(() => {
        fetchingErrorsFile.current[id] = false;
      });
  };

  const handleUpload = () => {
    if (entry.file) runValidation(entry.file);
  };

  const reset = () => {
    startRun(quoteId);
    clearStoredCensusEntry(quoteId);
    patch(quoteId, DEFAULT_ENTRY);
  };

  return {
    step: entry.step,
    file: entry.file,
    fileName: entry.fileName,
    requestId: entry.requestId,
    summary: entry.summary,
    errorsFile: entry.errorsFile,
    distribution: entry.distribution,
    priceMismatch: entry.priceMismatch,
    isRunning: entry.step === 'validating' || entry.step === 'processing',
    setFile,
    handleFileSelect: setFile,
    handleUpload,
    handleRetry: handleUpload,
    refreshErrorsFile,
    acceptNewPrice: () => patch(quoteId, { step: 'success' }),
    rejectNewPrice: reset,
    reset
  };
}
